import { withGst } from '@/shared/lib/currency';

import { agentInvoiceOf } from './outstanding';
import { ISSUED_BY_ASIC, PAY_STATUS, type OutstandingItem } from './outstanding.schema';
import { paymentRouteOf } from './rowActions';

export interface PaymentLine {
  label: string;
  amount: number;
}

/**
 * What the director is about to pay, itemised.
 *
 * An annual review settles two legs — the agent's fee (with GST) and ASIC's —
 * and either may already be paid, so a part-paid review shows only what is
 * left. The legacy dialog applied GST at the call site as
 * `Number(agentFee) * 1.1`; it goes through `withGst` here so the rate is
 * named in exactly one place.
 */
export const paymentLinesFor = (item: OutstandingItem): PaymentLine[] => {
  const route = paymentRouteOf(item);

  if (route === 'annualReview') {
    const lines: PaymentLine[] = [];
    if (item.agentInvoiceStatus !== PAY_STATUS.paid && item.agentAmount > 0) {
      lines.push({ label: 'Agent fee (incl. GST)', amount: withGst(item.agentAmount) });
    }
    if (item.asicInvoiceStatus !== PAY_STATUS.paid && item.asicAmount > 0) {
      lines.push({ label: 'ASIC fee', amount: item.asicAmount });
    }
    return lines;
  }

  if (route === 'formInvoice') {
    const invoice = item.invoices[0];
    if (invoice === undefined) return [];
    const label = agentInvoiceOf(item) === undefined ? 'Invoice' : 'Agent invoice';
    return [{ label, amount: invoice.amount }];
  }

  if (route === 'asicInvoice') {
    return [{ label: 'ASIC invoice', amount: item.invoices[0]?.amount ?? item.asicAmount }];
  }

  return [];
};

export const totalOf = (lines: PaymentLine[]): number =>
  lines.reduce((sum, line) => sum + line.amount, 0);

export interface InvoiceLink {
  id: string;
  label: string;
  href: string;
}

/**
 * The invoices behind the amount, as links.
 *
 * A director asked to approve a figure should be able to read the document it
 * came from before they pay it, which is what the legacy modal's "View Agent
 * Invoice" / "View ASIC Invoice" list offered. Invoices with no file are
 * dropped rather than rendered as dead links.
 */
export const invoiceLinksFor = (item: OutstandingItem): InvoiceLink[] =>
  item.invoices
    .filter((invoice) => invoice.fileURL !== '')
    .map((invoice) => ({
      id: invoice.id,
      label: invoice.issuedBy === ISSUED_BY_ASIC ? 'View ASIC invoice' : 'View agent invoice',
      href: invoice.fileURL,
    }));
